import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Persist React Query cache for the duration of the browser session.
// This allows polygon vessel results (and other queries) to survive reloads.
const hasWindow = typeof window !== 'undefined';
const hasSessionStorage = hasWindow && typeof window.sessionStorage !== 'undefined';

if (hasSessionStorage) {
  const persister = createSyncStoragePersister({
    storage: window.sessionStorage,
  });

  persistQueryClient({
    queryClient,
    persister,
    // How long persisted data is considered valid before being garbage-collected.
    // This is separate from per-query staleTime.
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    // Never persist errored queries so a page reload after a failure
    // will always trigger a fresh network request instead of restoring
    // a cached error state.
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => query.state.status === 'success',
    },
  });
}

/**
 * Reset React Query cache when authentication state changes.
 * This clears all in-memory queries and removes the persisted cache
 * so that a new login (or user switch) always starts from a clean slate.
 */
export function resetQueryClientForAuthChange() {
  queryClient.clear();

  if (!hasSessionStorage) return;

  try {
    // Default storage key used by @tanstack/react-query-persist-client
    // when no custom key is provided.
    window.sessionStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to clear persisted React Query cache on auth change:', error);
  }
}
